/**
 * THE PAGE'S OWN SCROLLBAR
 * ========================
 *
 * woodsidebible.org has no dark mode, but a laptop set to dark does: with no
 * `color-scheme` declared, the browser assumes the page can take it and paints
 * the scrollbar dark. The result is a black bar down the right edge of a white
 * site, for every visitor whose machine prefers dark — which is most of them.
 *
 * So the first thing here is the honest declaration the theme never made:
 * this page is light. That alone fixes the bar, and it fixes it for the whole
 * document rather than for the widget's own scrollers.
 *
 * THEN, WHERE AN EVENT HAS A COLOUR, THE BAR CAN CARRY IT. The header already
 * wears the event's colours while it stands on the artwork (lib/chrome.ts),
 * and the scrollbar runs the full height of that artwork. Where the event has
 * no colour of its own the bar is GREY, not teal: the church's action colour
 * means "press this", and a scrollbar is not a thing you press.
 *
 * NOTHING IS THE ACCENT AT FULL STRENGTH. The thumb is mixed halfway to a mid
 * grey, which pulls a dark navy up and a pale cream down and lands every
 * event in a range that reads as a scrollbar rather than as a stripe of
 * branding. The track is the same colour taken almost to white.
 *
 * IT IS OUR MESS TO CLEAN UP. The widget is a guest on somebody else's page,
 * so this returns the undo and takes both the property and the stylesheet off
 * together.
 */

import { isHexColor, mixHex } from './accent';

export const SCROLLBAR_FLAG = 'data-ed-scrollbar';

/**
 * Mid grey, and the reason the thumb works for any event.
 *
 * Mixing halfway toward this moves a colour TOWARD the middle from either
 * side: Flannel Fest's orange loses its glare, LOYA's near-black navy comes up
 * off the ground, and a pale cream comes down far enough to be seen against
 * the track. A mix toward white would have made the light ones invisible.
 */
const MID = '#8b929c';
/** The ground the track is mixed into: the site's white, a shade off. */
const PAPER = '#f2f3f5';

/** Grey, for every event with no colour of its own. Deliberately not the teal. */
const NEUTRAL_THUMB = '#b9bec6';
const NEUTRAL_TRACK = '#f1f2f4';

export interface ScrollbarVars {
  '--ed-sb-thumb': string;
  '--ed-sb-track': string;
}

/** The two values the bar wears. An event with no colour gets the grey pair. */
export function scrollbarVarsFor(accent: string | null | undefined): ScrollbarVars {
  if (!isHexColor(accent)) {
    return { '--ed-sb-thumb': NEUTRAL_THUMB, '--ed-sb-track': NEUTRAL_TRACK };
  }
  return {
    '--ed-sb-thumb': mixHex(accent, MID, 0.5),
    '--ed-sb-track': mixHex(accent, PAPER, 0.9),
  };
}

/**
 * A constant. The colours arrive as custom properties, already through
 * `isHexColor`, exactly as the header's do.
 *
 * `color-scheme` is set on the flag rather than only alongside the colours,
 * because it is the half that matters most and it has to hold even for an
 * event with no colour at all. `scrollbar-color` is Firefox and Chrome 121+;
 * Safari uses overlay scrollbars that never showed the black bar to begin
 * with, and simply ignores it.
 */
export const SCROLLBAR_CSS = `
html[${SCROLLBAR_FLAG}] {
  color-scheme: light;
  scrollbar-color: var(--ed-sb-thumb) var(--ed-sb-track);
}
`;

const STYLE_ID = 'ed-scrollbar';

/**
 * Tell the page it is light, tint its scrollbar, and hand back the undo.
 *
 * Runs for every event, with or without a colour: the black bar is not an
 * accent problem, it is a page that never said which mode it was in.
 */
export function paintScrollbar(doc: Document, accent: string | null | undefined): () => void {
  const root = doc.documentElement;
  if (!root) return () => {};

  let style = doc.getElementById(STYLE_ID);
  if (!style) {
    style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = SCROLLBAR_CSS;
    doc.head.appendChild(style);
  }

  for (const [name, value] of Object.entries(scrollbarVarsFor(accent))) {
    root.style.setProperty(name, value);
  }
  root.setAttribute(SCROLLBAR_FLAG, '');

  return () => {
    root.removeAttribute(SCROLLBAR_FLAG);
    root.style.removeProperty('--ed-sb-thumb');
    root.style.removeProperty('--ed-sb-track');
    doc.getElementById(STYLE_ID)?.remove();
  };
}
